import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Formik, useField } from 'formik';
import * as Yup from 'yup';

import BudgetTagsItemEditor from '../../budget_tags/BudgetTagsItemEditor';
import { budgetTagsErrorMessages, saveItemBudgetTags } from '../../budget_tags/itemBudgetTagsApi';
import { PacksContext, PacksDispatchContext } from '../ApiProvider';

import { saveAssigns } from 'Helpers/costPacks';

import Button from 'CommonComponents/Button';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';
import TextFormField from 'CommonComponents/TextFormField';

const SimplePackItemSchema = Yup.object().shape({
    name: Yup.string().required($t('This value is required')),
    price: Yup.number().moreThan(0).required($t('This value is required')),
});

const ContractorPackItemSchema = Yup.object().shape({
    name: Yup.string().required($t('This value is required')),
    price: Yup.number().moreThan(0).required($t('This value is required')),
    start_time_before: Yup.number().moreThan(0).label($t('Start time')),
    finish_time_after: Yup.number().moreThan(0).label($t('Finish time')),
    min_shift_duration: Yup.number().moreThan(0).label($t('Minimum shift duration')),
    max_shift_duration: Yup.number().moreThan(0).required($t('This value is required')).label($t('Max shift duration')),
    shift_overlap: Yup.number().min(0).label($t('Shift overlap duration')),
});

const categorySelectStyle = {
    display: 'block',
    width: '100%',
    height: '32px',
    padding: '6px 36px 6px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1B1C1D',
    backgroundColor: 'rgba(228, 228, 228, 0.5)',
    border: 'none',
    borderRadius: '8px',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%231B1C1D'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
};

function CategorySelectField({ categories, ...props }) {
    const [field, , helpers] = useField({ name: 'expense_pack_category_id', ...props });
    const value = field.value !== undefined && field.value !== null && field.value !== '' ? String(field.value) : '';
    return (
        <div className="form-group tw-flex tw-flex-col tw-space-y-1.5">
            <label htmlFor="expense_pack_category_id" className="text-input__label tw-mt-1">
                {$t('Category')}
            </label>
            <select
                id="expense_pack_category_id"
                name="expense_pack_category_id"
                style={categorySelectStyle}
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    helpers.setValue(v === '' ? null : parseInt(v, 10));
                }}
                onBlur={field.onBlur}
            >
                <option value="">{$t('— None —')}</option>
                {(categories || [])
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((cat) => (
                        <option key={cat.id} value={String(cat.id)}>
                            {cat.name}
                        </option>
                    ))}
            </select>
        </div>
    );
}

export default function PackItemForm({ id, name, price, type, budgetTagsUrl }) {
    return type === 'createSimpleItem' ? (
        <SimplePackItemForm id={id} budgetTagsUrl={budgetTagsUrl} />
    ) : (
        <ContractorPackItemForm id={id} budgetTagsUrl={budgetTagsUrl} />
    );
}

const isSavedItem = (id) => !!id && !String(id).startsWith('temp');

/**
 * The item's tags, saved on their own endpoint before the item itself. Opened from the
 * Packs page, so a pack item in several packs lists the tags of each.
 */
function usePackItemBudgetTags(budgetTagsUrl, itemId) {
    const value = useRef(null);
    const [errors, setErrors] = useState([]);
    const onChange = useCallback((next) => {
        value.current = next;
    }, []);

    const save = () => {
        if (!budgetTagsUrl || !isSavedItem(itemId) || !value.current) {
            return Promise.resolve(true);
        }
        setErrors([]);

        return saveItemBudgetTags(budgetTagsUrl, itemId, value.current)
            .then(() => true)
            .catch((error) => {
                setErrors(budgetTagsErrorMessages(error));

                return false;
            });
    };

    return { onChange, save, errors };
}

function PackItemBudgetTags({ budgetTagsUrl, itemId, tags }) {
    if (!budgetTagsUrl) {
        return null;
    }

    return (
        <div className="form-group tw-flex tw-flex-col tw-space-y-1.5">
            <span className="text-input__label tw-mt-1">{$t('Budget tags')}</span>
            {isSavedItem(itemId) ? (
                <BudgetTagsItemEditor id={`pack-item-${itemId}-tags`} url={budgetTagsUrl} itemId={itemId} onChange={tags.onChange} />
            ) : (
                <span className="tw-text-sm tw-italic tw-text-charcoal-75">{$t('Save the item before setting its tags')}</span>
            )}
            {tags.errors.map((message, index) => (
                <p key={index} className="tw-text-red tw-text-sm tw-mb-0">
                    {message}
                </p>
            ))}
        </div>
    );
}

function SimplePackItemForm({ id, budgetTagsUrl }) {
    const apiCall = React.useContext(PacksDispatchContext);

    const { simplePackItems, simplePackCategories } = useContext(PacksContext);

    const details = useMemo(() => {
        return id
            ? simplePackItems.find((item) => item.id == id) || { id: false, name: '', price: 0, expense_pack_category_id: null }
            : { id: false, name: '', price: 0, expense_pack_category_id: null };
    }, [simplePackItems, id]);

    const apiCallRef = React.useRef(apiCall);
    apiCallRef.current = apiCall;
    useEffect(() => {
        if (id && Array.isArray(simplePackItems) && simplePackItems.length > 0 && !simplePackItems.find((item) => item.id == id)) {
            apiCallRef.current('form', false);
        }
    }, [id, simplePackItems]);

    const budgetTags = usePackItemBudgetTags(budgetTagsUrl, details.id);

    const handleDelete = (value) => {
        apiCall('packItemDelete', details);
        saveAssigns();
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                name: details.name || '',
                price: details.price || '',
                expense_pack_category_id:
                    details.expense_pack_category_id != null && details.expense_pack_category_id !== '' ? details.expense_pack_category_id : null,
            }}
            validationSchema={SimplePackItemSchema}
            onSubmit={async (values, actions) => {
                if (!(await budgetTags.save())) {
                    return;
                }

                const data = {
                    ...details,
                    ...values,
                    expense_pack_category_id:
                        values.expense_pack_category_id !== undefined && values.expense_pack_category_id !== null && values.expense_pack_category_id !== ''
                            ? values.expense_pack_category_id
                            : null,
                };
                data.pack_contractor = 0;

                apiCall('packItemEdit', data);
                saveAssigns();
            }}
        >
            {({ values, errors, touched, isSubmitting, setFieldValue, handleSubmit }) => (
                <>
                    <TextFormField label={$t('Name')} type="text" required={true} name="name"></TextFormField>
                    <CategorySelectField categories={simplePackCategories} />
                    <div className="tw-flex tw-flex-col tw-space-y-1.5">
                        <label className="tw-mt-1 text-input__label" htmlFor="price">
                            {$t('Price')} *
                        </label>
                        <NumberFormat
                            name="price"
                            value={values.price}
                            defaultValue={0}
                            className={errors.price && touched.price ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                            decimalScale={10}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('price', value);
                            }}
                        />
                        {errors.price && touched.price && <div className="tw-text-red-500 tw-text-sm">{errors.price}</div>}
                    </div>
                    <PackItemBudgetTags budgetTagsUrl={budgetTagsUrl} itemId={details.id} tags={budgetTags} />

                    <div className="modal__footer">
                        <Button variant="secondary" onClick={() => apiCall('form', false)} disabled={isSubmitting}>
                            {$t('Cancel')}
                        </Button>
                        {!details.commitment_exists && (
                            <Button variant="danger" onClick={() => handleDelete()} disabled={isSubmitting}>
                                {$t('Delete')}
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                            {details.id ? $t('Update') : $t('Create')}
                        </Button>
                    </div>
                </>
            )}
        </Formik>
    );
}

function ContractorPackItemForm({ id, budgetTagsUrl }) {
    const apiCall = React.useContext(PacksDispatchContext);

    const { contractorPackItems, contractorPackCategories } = useContext(PacksContext);

    const details = useMemo(() => {
        return id
            ? contractorPackItems.find((item) => item.id == id) || { id: false, name: '', price: 0, expense_pack_category_id: null }
            : { id: false, name: '', price: 0, expense_pack_category_id: null };
    }, [contractorPackItems, id]);

    const budgetTags = usePackItemBudgetTags(budgetTagsUrl, details.id);

    const handleDelete = (value) => {
        apiCall('packItemDelete', details);
        saveAssigns();
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{
                name: details.name || '',
                price: details.price || '',
                expense_pack_category_id:
                    details.expense_pack_category_id != null && details.expense_pack_category_id !== '' ? details.expense_pack_category_id : null,
                start_time_before: details.start_time_before || '',
                finish_time_after: details.finish_time_after || '',
                min_shift_duration: details.min_shift_duration || '',
                max_shift_duration: details.max_shift_duration || '',
                shift_overlap: details.shift_overlap === undefined ? '' : details.shift_overlap,
            }}
            validationSchema={ContractorPackItemSchema}
            onSubmit={async (values, actions) => {
                if (!(await budgetTags.save())) {
                    return;
                }

                const data = {
                    ...details,
                    ...values,
                    expense_pack_category_id:
                        values.expense_pack_category_id !== undefined && values.expense_pack_category_id !== null && values.expense_pack_category_id !== ''
                            ? values.expense_pack_category_id
                            : null,
                };
                data.pack_contractor = 1;

                apiCall('packItemEdit', data);
                saveAssigns();
            }}
        >
            {({ values, errors, touched, isSubmitting, setFieldValue, handleSubmit }) => (
                <>
                    <TextFormField label={$t('Description')} type="text" required={true} name="name"></TextFormField>
                    <CategorySelectField categories={contractorPackCategories} />
                    <div className="form-group">
                        <label className="tw-mt-1 text-input__label" htmlFor="price">
                            {$t('Rate Per Hour')} *
                        </label>
                        <NumberFormat
                            name="price"
                            value={values.price}
                            defaultValue={0}
                            className={errors.price && touched.price ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                            decimalScale={10}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('price', value);
                            }}
                        />
                        {errors.price && touched.price && <div className="tw-text-red-500 tw-text-sm">{errors.price}</div>}
                    </div>
                    <div className="form-group">
                        <label className="tw-mt-1 text-input__label" htmlFor="start_time">
                            {$t('Start Time Prior to Session Starting (hrs)')}
                        </label>
                        <NumberFormat
                            name="start_time_before"
                            value={values.start_time_before}
                            defaultValue={0}
                            className={
                                errors.start_time_before && touched.start_time_before ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                            }
                            decimalScale={2}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('start_time_before', value);
                            }}
                        />
                        {errors.start_time_before && touched.start_time_before && <div className="tw-text-red-500 tw-text-sm">{errors.start_time_before}</div>}
                    </div>
                    <div className="form-group">
                        <label className="tw-mt-1 text-input__label" htmlFor="finish_time">
                            {$t('Finish Time After Session Finishes (hrs)')}
                        </label>
                        <NumberFormat
                            name="finish_time_after"
                            value={values.finish_time_after}
                            defaultValue={0}
                            className={
                                errors.finish_time_after && touched.finish_time_after ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                            }
                            decimalScale={2}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('finish_time_after', value);
                            }}
                        />
                        {errors.finish_time_after && touched.finish_time_after && <div className="tw-text-red-500 tw-text-sm">{errors.finish_time_after}</div>}
                    </div>
                    <div className="form-group">
                        <label className="tw-mt-1 text-input__label" htmlFor="min_shift_duration">
                            {$t('Minimum shift duration')}
                        </label>
                        <NumberFormat
                            name="min_shift_duration"
                            value={values.min_shift_duration}
                            defaultValue={0}
                            className={
                                errors.min_shift_duration && touched.min_shift_duration ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                            }
                            decimalScale={2}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('min_shift_duration', value);
                            }}
                        />
                        {errors.min_shift_duration && touched.min_shift_duration && (
                            <div className="tw-text-red-500 tw-text-sm">{errors.min_shift_duration}</div>
                        )}
                    </div>
                    <div className="form-group">
                        <label className="tw-mt-1 text-input__label" htmlFor="max_shift_duration">
                            {$t('Max shift duration')} *
                        </label>
                        <NumberFormat
                            name="max_shift_duration"
                            value={values.max_shift_duration}
                            defaultValue={0}
                            className={
                                errors.max_shift_duration && touched.max_shift_duration ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                            }
                            decimalScale={2}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('max_shift_duration', value);
                            }}
                        />
                        {errors.max_shift_duration && touched.max_shift_duration && (
                            <div className="tw-text-red-500 tw-text-sm">{errors.max_shift_duration}</div>
                        )}
                    </div>
                    <div className="form-group">
                        <label className="tw-mt-1 text-input__label" htmlFor="shift_overlap_duration">
                            {$t('Shift Overlap Period')}
                        </label>
                        <NumberFormat
                            name="shift_overlap"
                            value={values.shift_overlap}
                            defaultValue={0}
                            className={errors.shift_overlap && touched.shift_overlap ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                            decimalScale={2}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            onValueChange={(values) => {
                                const { value } = values;
                                setFieldValue('shift_overlap', value);
                            }}
                        />
                        {errors.shift_overlap && touched.shift_overlap && <div className="tw-text-red-500 tw-text-sm">{errors.shift_overlap}</div>}
                    </div>
                    <PackItemBudgetTags budgetTagsUrl={budgetTagsUrl} itemId={details.id} tags={budgetTags} />

                    <div className="modal__footer">
                        <Button variant="secondary" onClick={() => apiCall('form', false)} disabled={isSubmitting}>
                            {$t('Cancel')}
                        </Button>
                        {!details.commitment_exists && (
                            <Button variant="danger" onClick={() => handleDelete()} disabled={isSubmitting}>
                                {$t('Delete')}
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                            {details.id ? $t('Update') : $t('Create')}
                        </Button>
                    </div>
                </>
            )}
        </Formik>
    );
}
